import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useAuth} from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMenu, FiX, FiUser, FiLogOut, FiSettings, FiShield} = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const {user, logout} = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  const navLinks = [
    {name: 'Learning', path: '/learning'},
    {name: 'Events', path: '/events'},
    {name: 'Candidates', path: '/candidates'},
    {name: 'Community', path: '/community'},
    {name: 'Representatives', path: '/representatives/lookup'},
    {name: 'Resources', path: '/resources'}
  ];

  return (
    <>
      {/* Skip Link for Accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      
      <nav className="bg-white shadow-lg sticky top-0 z-50 border-b-2 border-patriot-red" role="navigation" aria-label="Main navigation" style={{zIndex: 1000}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3 group" aria-label="The GOP Zone Home">
                <div className="w-10 h-10 bg-gradient-to-br from-patriot-red to-patriot-navy rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-300">
                  <SafeIcon icon={FiShield} className="text-white h-6 w-6" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-patriot-navy font-display">The GOP Zone</span>
                  <span className="text-xs text-patriot-red font-medium">Republican Platform</span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-gray-700 hover:text-patriot-red transition-colors duration-300 font-medium text-sm"
                  aria-label={link.name}
                >
                  {link.name}
                </Link>
              ))}

              {/* Advertise Button for Candidates */}
              <Link
                to="/advertise"
                className="bg-patriot-gold text-patriot-navy px-3 py-2 rounded-md hover:bg-patriot-gold-dark transition-all duration-300 font-medium text-sm"
              >
                Advertise
              </Link>
            </div>

            {/* User Menu & Auth */}
            <div className="flex items-center space-x-4">
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-patriot-red transition-colors duration-300 p-2 rounded-md hover:bg-red-50"
                    aria-expanded={showUserMenu}
                    aria-haspopup="true"
                    aria-label="User menu"
                  >
                    <img
                      src={user.avatar}
                      alt={`${user.name}'s avatar`}
                      className="w-8 h-8 rounded-full border-2 border-patriot-red shadow-sm"
                    />
                    <span className="hidden md:block font-medium text-sm">{user.name}</span>
                  </button>

                  {showUserMenu && (
                    <motion.div
                      initial={{opacity: 0, y: -10, scale: 0.95}}
                      animate={{opacity: 1, y: 0, scale: 1}}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-200"
                      role="menu"
                      aria-orientation="vertical"
                      style={{zIndex: 1100}}
                    >
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-patriot-red transition-colors duration-200"
                        onClick={() => setShowUserMenu(false)}
                        role="menuitem"
                      >
                        <SafeIcon icon={FiUser} className="mr-3 h-4 w-4" aria-hidden="true" />
                        Profile
                      </Link>
                      <Link
                        to="/dashboard"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-patriot-red transition-colors duration-200"
                        onClick={() => setShowUserMenu(false)}
                        role="menuitem"
                      >
                        <SafeIcon icon={FiSettings} className="mr-3 h-4 w-4" aria-hidden="true" />
                        Dashboard
                      </Link>
                      <hr className="my-1 border-gray-200" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                        role="menuitem"
                      >
                        <SafeIcon icon={FiLogOut} className="mr-3 h-4 w-4" aria-hidden="true" />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </div>
              ) : (
                <div className="hidden md:flex items-center space-x-3">
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-patriot-red transition-colors duration-300 font-medium text-sm px-3 py-2"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-patriot-red text-white px-4 py-2 rounded-md hover:bg-patriot-red-dark transition-all duration-300 font-medium text-sm"
                  >
                    Join GOP
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden text-gray-700 hover:text-patriot-red transition-colors duration-300 p-2 rounded-md hover:bg-red-50"
                aria-expanded={isOpen}
                aria-label="Toggle mobile menu"
              >
                <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <motion.div
              initial={{opacity: 0, height: 0}}
              animate={{opacity: 1, height: 'auto'}}
              className="lg:hidden border-t border-gray-200 mt-2"
            >
              <div className="px-2 pt-2 pb-3 space-y-1 bg-red-50 rounded-md mt-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className="block px-3 py-2 text-gray-700 hover:text-patriot-red hover:bg-white transition-colors duration-300 font-medium rounded-md text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                
                <Link
                  to="/advertise"
                  className="block px-3 py-2 bg-patriot-gold text-patriot-navy rounded-md hover:bg-patriot-gold-dark transition-all duration-300 font-medium text-center text-sm"
                  onClick={() => setIsOpen(false)}
                >
                  Advertise Your Campaign
                </Link>

                {!user && (
                  <>
                    <Link
                      to="/login"
                      className="block px-3 py-2 text-gray-700 hover:text-patriot-red hover:bg-white transition-colors duration-300 font-medium rounded-md text-sm"
                      onClick={() => setIsOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      className="block px-3 py-2 bg-patriot-red text-white rounded-md hover:bg-patriot-red-dark transition-all duration-300 font-medium text-center text-sm"
                      onClick={() => setIsOpen(false)}
                    >
                      Join GOP
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;